import { JournalEntryFormData, JournalValidationError } from '../types/journal';

/**
 * Parses a value that may be a number or string into a number.
 * Returns NaN if the value cannot be parsed.
 */
function parseNumeric(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return NaN;
  if (typeof value === 'number') return value;
  const trimmed = value.trim();
  if (trimmed === '') return NaN;
  return Number(trimmed);
}

/**
 * Checks if a numeric value (which may come as string from a form input) is present and non-empty.
 */
function isPresent(value: number | string | null | undefined): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  return true;
}

/**
 * Validates a journal entry form data object and returns an array of validation errors.
 * An empty array means the data is valid.
 *
 * Handles both number and string inputs for numeric fields (since form inputs
 * produce strings). Optional fields are only validated when present/non-empty.
 */
export function validateJournalEntry(data: JournalEntryFormData): JournalValidationError[] {
  const errors: JournalValidationError[] = [];

  // servingSize: Required, number, 0.01–999.99
  if (!isPresent(data.servingSize)) {
    errors.push({ field: 'servingSize', message: 'Serving size is required' });
  } else {
    const serving = parseNumeric(data.servingSize);
    if (isNaN(serving)) {
      errors.push({ field: 'servingSize', message: 'Serving size must be a valid number' });
    } else if (serving < 0.01 || serving > 999.99) {
      errors.push({ field: 'servingSize', message: 'Serving size must be between 0.01 and 999.99' });
    }
  }

  // cannabinoidMg: Required, number, 0–10000
  if (!isPresent(data.cannabinoidMg)) {
    errors.push({ field: 'cannabinoidMg', message: 'Cannabinoid amount is required' });
  } else {
    const mg = parseNumeric(data.cannabinoidMg);
    if (isNaN(mg)) {
      errors.push({ field: 'cannabinoidMg', message: 'Cannabinoid amount must be a valid number' });
    } else if (mg < 0 || mg > 10000) {
      errors.push({ field: 'cannabinoidMg', message: 'Cannabinoid amount must be between 0 and 10,000 mg' });
    }
  }

  // cannabinoidType: Required, non-empty string, max 50 chars
  if (!data.cannabinoidType || data.cannabinoidType.trim().length === 0) {
    errors.push({ field: 'cannabinoidType', message: 'Cannabinoid type is required' });
  } else if (data.cannabinoidType.length > 50) {
    errors.push({ field: 'cannabinoidType', message: 'Cannabinoid type must be 50 characters or fewer' });
  }

  // consumedAt: Required, valid ISO datetime, not in the future
  if (!data.consumedAt || data.consumedAt.trim().length === 0) {
    errors.push({ field: 'consumedAt', message: 'Consumption date and time is required' });
  } else {
    const date = new Date(data.consumedAt);
    if (isNaN(date.getTime())) {
      errors.push({ field: 'consumedAt', message: 'Consumption date and time must be a valid date' });
    } else {
      // Allow 1 minute buffer for clock skew
      const now = new Date();
      const bufferMs = 60 * 1000;
      if (date.getTime() > now.getTime() + bufferMs) {
        errors.push({ field: 'consumedAt', message: 'Consumption date and time cannot be in the future' });
      }
    }
  }

  // onsetMinutes: Optional, integer, 0–1440
  if (isPresent(data.onsetMinutes)) {
    const onset = parseNumeric(data.onsetMinutes);
    if (isNaN(onset)) {
      errors.push({ field: 'onsetMinutes', message: 'Onset time must be a valid number' });
    } else if (!Number.isInteger(onset)) {
      errors.push({ field: 'onsetMinutes', message: 'Onset time must be a whole number' });
    } else if (onset < 0 || onset > 1440) {
      errors.push({ field: 'onsetMinutes', message: 'Onset time must be between 0 and 1,440 minutes' });
    }
  }

  // peakMinutes: Optional, integer, 0–1440
  if (isPresent(data.peakMinutes)) {
    const peak = parseNumeric(data.peakMinutes);
    if (isNaN(peak)) {
      errors.push({ field: 'peakMinutes', message: 'Peak time must be a valid number' });
    } else if (!Number.isInteger(peak)) {
      errors.push({ field: 'peakMinutes', message: 'Peak time must be a whole number' });
    } else if (peak < 0 || peak > 1440) {
      errors.push({ field: 'peakMinutes', message: 'Peak time must be between 0 and 1,440 minutes' });
    }
  }

  // durationMinutes: Optional, integer, 0–1440
  if (isPresent(data.durationMinutes)) {
    const duration = parseNumeric(data.durationMinutes);
    if (isNaN(duration)) {
      errors.push({ field: 'durationMinutes', message: 'Duration must be a valid number' });
    } else if (!Number.isInteger(duration)) {
      errors.push({ field: 'durationMinutes', message: 'Duration must be a whole number' });
    } else if (duration < 0 || duration > 1440) {
      errors.push({ field: 'durationMinutes', message: 'Duration must be between 0 and 1,440 minutes' });
    }
  }

  // notes: Optional, max 2000 chars
  if (data.notes && data.notes.length > 2000) {
    errors.push({ field: 'notes', message: 'Notes must be 2,000 characters or fewer' });
  }

  // rating: Optional, integer, 1–5
  if (data.rating !== null && data.rating !== undefined) {
    if (!Number.isInteger(data.rating)) {
      errors.push({ field: 'rating', message: 'Rating must be a whole number' });
    } else if (data.rating < 1 || data.rating > 5) {
      errors.push({ field: 'rating', message: 'Rating must be between 1 and 5' });
    }
  }

  // effects (each): Max 50 chars
  // effects (total): Max 20 items
  if (data.effects && data.effects.length > 20) {
    errors.push({ field: 'effects', message: 'Maximum of 20 effects allowed' });
  }
  if (data.effects) {
    for (const effect of data.effects) {
      if (effect.length > 50) {
        errors.push({ field: 'effects', message: 'Each effect must be 50 characters or fewer' });
        break; // One error message is sufficient for the field
      }
    }
  }

  return errors;
}
